import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ErrorBanner } from '@/shared/ui/ErrorBanner';
import { safeErrorMessage } from '@/shared/utils/errors';
import { statusBadgeClass } from '@/shared/utils/statusBadge';
import { DataTable } from '@/shared/ui/DataTable';
import { createDataTableColumns } from '@/shared/ui/dataTableFeatures';
import { usePaginatedList } from '@/shared/hooks/usePaginatedList';
import { listVideoJobs, revertVideoCredits, getUser } from '@/features/admin/services/adminService';
import type { AdminVideoJob } from '@/shared/types';

const PAGE_SIZE = 200;

function formatJobDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const jobsColumnHelper = createDataTableColumns<AdminVideoJob>();

function buildJobsColumns(revertingId: string | null, onRevert: (jobId: string) => void) {
  return jobsColumnHelper.columns([
    jobsColumnHelper.accessor((j) => formatJobDate(j.createdAt), {
      id: 'createdAt',
      header: 'Date',
      filterFn: 'includesString',
    }),
    jobsColumnHelper.accessor((j) => `${j.title || ''} ${j.jobId}`, {
      id: 'jobIdTitle',
      header: 'Job ID / Title',
      filterFn: 'includesString',
      cell: ({ row }) => {
        const j = row.original;
        return j.title ? (
          <div>
            <div>{j.title}</div>
            <div className="field-note">{j.jobId}</div>
          </div>
        ) : (
          <span className="field-note">{j.jobId}</span>
        );
      },
    }),
    jobsColumnHelper.accessor('status', {
      header: 'Status',
      filterFn: 'equals',
      meta: {
        filterType: 'select',
        filterOptions: [
          { label: 'Queued', value: 'queued' },
          { label: 'Processing', value: 'processing' },
          { label: 'Completed', value: 'completed' },
          { label: 'Failed', value: 'failed' },
          { label: 'Cancelled', value: 'cancelled' },
        ],
      },
      cell: (info) => <span className={statusBadgeClass(info.getValue())}>{info.getValue()}</span>,
    }),
    jobsColumnHelper.accessor('totalCostUsd', {
      header: 'Cost',
      filterFn: 'includesString',
      cell: (info) => `$${info.getValue().toFixed(2)}`,
    }),
    jobsColumnHelper.display({
      id: 'actions',
      header: 'Actions',
      enableColumnFilter: false,
      meta: { filterType: 'none' },
      cell: ({ row }) => {
        const j = row.original;
        return (
          <div className="admin-table-actions">
            {j.status === 'failed' && (
              <button
                type="button"
                className="button secondary admin-table-actions__compact"
                onClick={() => onRevert(j.jobId)}
                disabled={revertingId === j.jobId}
              >
                {revertingId === j.jobId ? 'Reverting…' : 'Revert credits'}
              </button>
            )}
          </div>
        );
      },
    }),
  ]);
}

export default function AdminUserJobsPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // The Users table already has this row's email in memory — passed via nav
  // state to avoid a redundant GET /admin/users/{id} just to redisplay it here.
  // Falls back to fetching it only when state is absent (a direct link/refresh).
  const stateEmail = (location.state as { email?: string } | null)?.email;
  const [userEmail, setUserEmail] = useState<string | null>(stateEmail ?? null);

  const [notice, setNotice] = useState<string | null>(null);
  const [revertingId, setRevertingId] = useState<string | null>(null);

  const { items: jobs, loading, loadingMore, error, setError, hasMore, load, loadMore } =
    usePaginatedList<AdminVideoJob>((nextOffset) => {
      if (!userId) return Promise.resolve({ items: [], total: 0, offset: 0 });
      return listVideoJobs({
        userId,
        limit: PAGE_SIZE,
        offset: nextOffset,
      });
    });

  useEffect(() => {
    if (!userId) return;
    if (!stateEmail) {
      getUser(userId)
        .then((u) => setUserEmail(u.email))
        .catch(() => { }); // Suppress user fetch error since we can fall back to userId
    }
    load(0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleRevert = useCallback(
    async (id: string) => {
      setRevertingId(id);
      setError(null);
      setNotice(null);
      try {
        await revertVideoCredits(id);
        setNotice('Credits reverted successfully.');
        load(0, false);
      } catch (err) {
        setError(safeErrorMessage(err, 'Unable to revert credits for this job.'));
      } finally {
        setRevertingId(null);
      }
    },
    [load, setError],
  );

  const columns = useMemo(() => buildJobsColumns(revertingId, handleRevert), [revertingId, handleRevert]);

  return (
    <section className="admin-users">
      <div className="admin-section-gap-sm">
        <button type="button" className="button secondary" onClick={() => navigate('/admin')}>
          &larr; Back to Users
        </button>
      </div>

      <div className="admin-header">
        <h1>Video Jobs for {userEmail || userId || 'User'}</h1>
      </div>

      {notice && (
        <div className="empty-state admin-section-gap-sm">
          <div className="empty-state__title">{notice}</div>
        </div>
      )}
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner" />
        </div>
      ) : jobs.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__title">No video jobs found</div>
        </div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={jobs}
            getRowId={(j) => j.jobId}
            className="admin-section-gap-sm"
            enablePagination={true}
            pageSize={20}
          />

          {hasMore && (
            <button
              type="button"
              className="button secondary wallet-activity__more"
              onClick={loadMore}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading more…' : 'Load more from server'}
            </button>
          )}
        </>
      )}
    </section>
  );
}
